import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap text-sm font-medium transition-all duration-150 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-40',
  {
    variants: {
      variant: {
        default:     'bg-primary text-primary-foreground hover:bg-primary-dim active:bg-primary',
        destructive: 'bg-cmd-error text-white hover:bg-[#ff5550] active:bg-cmd-error',
        outline:     'border border-cmd-outline bg-transparent text-cmd-outline hover:text-white',
        secondary:   'bg-cmd-highest text-cmd-outline hover:text-white',
        ghost:       'text-cmd-outline hover:bg-cmd-high hover:text-white',
        link:        'text-primary underline-offset-4 hover:underline',
        success:     'bg-[#00c853] text-[#003d20] hover:bg-[#00e676] active:bg-[#00c853]',
      },
      size: {
        default:  'h-9 px-4 py-2',
        sm:       'h-7 px-3 text-xs',
        lg:       'h-10 px-8',
        icon:     'h-9 w-9',
        'icon-sm':'h-7 w-7',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
  }
)
Button.displayName = 'Button'

export { Button, buttonVariants }
